import { Capacitor } from '@capacitor/core';
import { ModalController, Platform } from '@ionic/angular';
import { ProductDetailViewModel } from '@product-detail/models/product-detail-view-model';
import { LoadingSpinnerService } from '@shared/services/loading-spinner/loading-spinner.service';
import { ModalService } from '@shared/services/modal/modal.service';
import { PlatformService } from '@panamax/app-state';
import { ModalWithNavComponent } from '@shared/components/modal-with-nav/modal-with-nav.component';
import { MerchPrintModalComponent } from '@shared/components/merch-print-modal/merch-print-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { SentenceCasePipe } from '../../shared/pipes/sentence-case.pipe';
import { ServiceHandlerService } from '../../shared/services/service-handler.service';
import { ImageConverterHelpersService } from '../../shared/helpers/image-converter.helpers.service';
import {
  PrintProductCompareDetail,
  PrintProductCompareModel,
  ProductComparePrinter,
  ProductComparePrinterConfig,
  ProductCompareRowLabels,
} from '@usf/file-share-utils';
import { ImageVariantEnum } from '../../product-detail/models/image-variant.enum';
import { environment } from '../../../environments/environment';
import { ProductPropertiesEnum } from '@usf/product-types';
import { getProductStatusPills } from '../../shared/helpers/document-creation.helpers';
import { getFirstImageURL } from '../../product-detail/utils/product-images.util';

export type PdfFile = {
  fileName: string;
  data: Blob;
};

export abstract class ProductComparePdfHandlerService {
  constructor(
    public platform: Platform,
    public loadingSpinnerService: LoadingSpinnerService,
    protected translateService: TranslateService,
    protected sentenceCasePipe: SentenceCasePipe,
    private serviceHandler: ServiceHandlerService,
    private imageConverterService: ImageConverterHelpersService,
    protected modalController: ModalController,
    protected modalService: ModalService,
    protected platformService: PlatformService,
  ) {}

  // abstract template functions
  protected abstract getRootPageParams(): {
    title: string;
    confirmButton: string;
  };
  protected abstract handleNative(pdfFile: PdfFile): void;
  protected abstract handleNonNative(pdfFile: PdfFile): void;
  protected abstract generateFileName(
    products: ProductDetailViewModel[],
  ): string;
  protected abstract runAnalytics(): void;
  protected abstract doCleanup(): void;

  public async showPdfPrompt(products: ProductDetailViewModel[]) {
    this.modalService.setModalOptions(
      this.platformService.isTouch.value,
      ModalWithNavComponent,
      {
        rootPage: MerchPrintModalComponent,
        rootPageParams: this.getRootPageParams(),
      },
      'modal-desktop-size-sm',
    );
    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    await modal.present();
    await modal.onDidDismiss().then((resp: any) => {
      if (!!resp?.data && resp?.data?.includeProductPrices !== undefined) {
        this.doPdfAction(products, resp?.data?.includeProductPrices);
      }
    });
  }

  private doPdfAction(
    productDetails: ProductDetailViewModel[],
    includeProductPrices: boolean,
  ) {
    this.loadingSpinnerService
      .createSpinnerModal()
      .then(async () => {
        const pdfFile: PdfFile = await this.createPDF(
          productDetails,
          includeProductPrices,
        );
        const isMobile = Capacitor.isNativePlatform();
        if (isMobile) {
          this.handleNative(pdfFile);
        } else {
          this.handleNonNative(pdfFile);
        }
        this.runAnalytics();
      })
      .finally(() => {
        this.doCleanup();
      });
  }

  private async createPDF(
    productDetails: ProductDetailViewModel[],
    includeProductPrices: boolean,
  ): Promise<PdfFile> {
    const rawData: Blob = await this.getProductComparePrinter().createPDFBlob(
      this.getPrintProductCompareModel(productDetails, includeProductPrices),
    );
    return {
      fileName: this.generateFileName(productDetails),
      data: rawData,
    };
  }

  protected getPrintProductCompareModel(
    productDetails: ProductDetailViewModel[],
    includeProductPrices: boolean,
  ): PrintProductCompareModel {
    const documentHeaderTitle = this.getTranslatedRowLabel('title');

    const rowLabels = this.getRowLabels();

    const productCompareDetails =
      this.getPrintProductCompareDetails(productDetails);

    const productImageUrls = productDetails?.map(productDetail =>
      getFirstImageURL(productDetail?.summary, ImageVariantEnum.Medium),
    );

    const productStatusPills = productCompareDetails?.map(
      printProductCompareModel => printProductCompareModel?.productStatusPills,
    );

    return {
      fileName: this.generateFileName(productDetails),
      documentHeaderTitle,
      includeProductPrices,
      rowLabels,
      productCompareDetails,
      productImageUrls,
      productStatusPills,
    };
  }

  private getProductComparePrinter(): ProductComparePrinter {
    const printerConfig = {
      imageBase64ApiUrl: environment.imageBase64ApiUrl,
      assetsBaseUrl: environment.webDomain,
      serviceHandler: this.serviceHandler,
      imageConverter: this.imageConverterService,
    } as ProductComparePrinterConfig;
    return new ProductComparePrinter(printerConfig);
  }

  private getTranslatedRowLabel(stringPath: string): string {
    return this.translateService.instant(
      `i18n.search.searchCompare.${stringPath}`,
    );
  }

  private getRowLabels(): ProductCompareRowLabels {
    return {
      productNumber: this.getTranslatedRowLabel('productNumber'),
      productDescription: this.getTranslatedRowLabel('productDescription'),
      brandName: this.getTranslatedRowLabel('brand'),
      price: this.getTranslatedRowLabel('price'),
      cases: this.getTranslatedRowLabel('cases'),
      eaches: this.getTranslatedRowLabel('eaches'),
      packSize: this.getTranslatedRowLabel('packSize'),
      pricePerPortion: this.getTranslatedRowLabel('pricePerPortion'),
      availableInventory: this.getTranslatedRowLabel('availableInventory'),
      productAttributes: this.getTranslatedRowLabel('productAttributes'),
      purchaseHistory: this.getTranslatedRowLabel('purchaseHistory'),
      netWeight: this.getTranslatedRowLabel('netWeight'),
      usfStorage: this.getTranslatedRowLabel('usfStorage'),
      ingredients: this.getTranslatedRowLabel('ingredients'),
      nutritionalFacts: this.getTranslatedRowLabel('nutritionalFacts'),
    };
  }

  private getPrintProductCompareDetails(
    productDetails: ProductDetailViewModel[],
  ): PrintProductCompareDetail[] {
    return productDetails?.map(productDetail => {
      const productNumber = productDetail?.productNumber;

      const productDescription = this.sentenceCasePipe.transform(
        `${productDetail?.summary?.productDescTxtl}`,
        'ALL',
      );
      const brandName = this.sentenceCasePipe.transform(
        `${productDetail?.summary?.brand}`,
        'ALL',
      );

      let priceCases: string;
      let priceEaches: string;
      if (
        productDetail?.summary?.properties?.has(
          ProductPropertiesEnum.specialOrder,
        )
      ) {
        priceCases = this.getProductCompareDataById(
          'specialPrice',
          productNumber,
        );
        priceEaches = '';
      } else {
        priceCases = this.getProductCompareDataById('casePrice', productNumber);
        priceEaches = this.getProductCompareDataById(
          'eachPrice',
          productNumber,
        );
      }

      const pricePerPortion = this.getProductCompareDataById(
        'pricePerPortion',
        productNumber,
      );
      const availableInventoryCases = this.getProductCompareDataById(
        'casesInventory',
        productNumber,
      );
      const availableInventoryEaches = this.getProductCompareDataById(
        'eachesInventory',
        productNumber,
      );
      const productStatusPills = getProductStatusPills(productDetail)?.map(
        productStatusPill => {
          if (productStatusPill?.type === 'url') {
            return productStatusPill;
          } else {
            return {
              ...productStatusPill,
              path: productStatusPill?.path?.split('/')?.pop(),
            };
          }
        },
      );
      const purchaseHistory = this.getProductCompareDataById(
        'purchaseHistory',
        productNumber,
      );

      return {
        productDetail,
        productDescription,
        brandName,
        priceCases,
        priceEaches,
        pricePerPortion,
        availableInventoryCases,
        availableInventoryEaches,
        productStatusPills,
        purchaseHistory,
      };
    });
  }

  private getProductCompareDataById(
    idPrefix: string,
    productNumber: number,
  ): string {
    return document.getElementById(`${idPrefix}-${productNumber}`)?.innerText;
  }
}
